<script lang="ts" setup>
import { onMounted, watch, computed } from 'vue';

import { isAirportSuggestion } from '@libs/trip-planner-interfaces';
import type {
  Coordinate,
  FlightAutoCompleteSuggestion,
} from '@libs/trip-planner-interfaces';
import { AppInputAutosuggestDropdown } from '@libs/ui';

import { useAutoCompleteStore } from './stores/autoCompleteStore';
import type { AutoCompleteType } from './AutoComplete.model';

const autoCompleteStore = useAutoCompleteStore();

interface AutoCompleteSuggestionsProps {
  autoCompleteType: AutoCompleteType;
  initialSearchText: string;
  placeholder?: string;
  initialUserPosition?: Coordinate;
  showBackButton?: boolean;
  disable?: boolean;
  shouldFocusOnInit?: boolean;
  wrapperBottomBoundary?: number;
}

const props = defineProps<AutoCompleteSuggestionsProps>();

interface AutoCompleteSuggestionsEmits {
  (
    eventName: 'airport-selected',
    airportSuggestion: FlightAutoCompleteSuggestion
  ): void;
  (eventName: 'close'): void;
  (eventName: 'on-input-click'): void;
}

const emit = defineEmits<AutoCompleteSuggestionsEmits>();

const suggestions = computed(
  () =>
    autoCompleteStore.autoCompleteSuggestions?.[0]?.data?.map(
      (suggestion, index) => ({
        label: suggestion.label,
        index,
      })
    ) ?? []
);

const dropdownTitle = computed(
  () => autoCompleteStore.autoCompleteSuggestions?.[0]?.label
);

function updateModelValue(value: string): void {
  autoCompleteStore.searchText = value;
  autoCompleteStore.setSearchText(value);
}

watch(
  () => props.initialUserPosition,
  (position) => {
    if (position) {
      autoCompleteStore.setSearchPosition(position);
    }
  },
  { immediate: true }
);

function handleSelect(item: { label: string; index: number }): void {
  const suggestion =
    autoCompleteStore.autoCompleteSuggestions?.[0]?.data?.[item.index];

  if (suggestion && isAirportSuggestion(suggestion)) {
    emit('airport-selected', suggestion);
  }
}

function handleClose(): void {
  autoCompleteStore.cleanAutoCompleteSuggestions();
  emit('close');
}

function handleOpen(ifInputFocused: boolean): void {
  if (ifInputFocused) {
    autoCompleteStore.initializeAutoComplete(
      props.autoCompleteType,
      props.initialSearchText
    );
  }
  emit('on-input-click');
}

onMounted(() => {
  autoCompleteStore.cleanAutoCompleteSuggestions();
});
</script>

<template>
  <AppInputAutosuggestDropdown
    :items="suggestions"
    :input-text="props.initialSearchText"
    :placeholder="props.placeholder"
    :max-visible-items="5"
    theme="neutral"
    :disable="props.disable"
    highlight
    :should-focus-on-init="props.shouldFocusOnInit"
    :wrapper-bottom-boundary="wrapperBottomBoundary"
    @update-model-value="updateModelValue"
    @select="handleSelect"
    @close="handleClose"
    @on-input-focus="handleOpen"
  >
    <template #left-icon>
      <slot name="left-icon" />
    </template>
    <template #dropdown-title>
      {{ dropdownTitle }}
    </template>
  </AppInputAutosuggestDropdown>
</template>
